import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { MITPService } from "./mitp.service";
import { BehaviorSubject, Observable, of, map, tap } from "rxjs";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Router } from "@angular/router";
import { User } from "../interfaces/users/user";
import { MitpManagerService } from "./mitp-manager.service";
import * as Sentry from "@sentry/angular";
import { IToken } from "../interfaces/itoken";
import { AdminService } from "./admin.service";
import { ProtocolService } from "./protocol.service";
import { UserService } from "./user.service";
import { MatDialog } from "@angular/material/dialog";

export interface PasskeyLoginOptionsResponse {
	status: string;
	errorMessage?: string;
	challenge: string;
	rpId?: string;
	timeout?: number;
	userVerification?: UserVerificationRequirement;
	allowCredentials?: Array<{
		id: string;
		type: PublicKeyCredentialType;
		transports?: AuthenticatorTransport[];
	}>;
}

export interface PasskeyLoginPayload {
	id: string;
	rawId: string;
	type: PublicKeyCredentialType;
	response: {
		clientDataJSON: string;
		authenticatorData: string;
		signature: string;
		userHandle: string | null;
	};
}

export interface PasskeyLoginResponse {
	status: string;
	errorMessage?: string;
	token?: string;
}

/**
 * Credentials carried by a magic link, kept exactly as they appear in the address bar.
 * The backend validates the signature against these raw values, so they must never be
 * decoded, re-encoded, trimmed or reordered on the way back to the API.
 */
export interface MagicLinkCredentials {
	user: string;
	expires: string;
	hash: string;
}

@Injectable({
	providedIn: "root",
})
export class AuthService {
	private http = inject(HttpClient);
	private adminService = inject(AdminService);
	private mitp = inject(MITPService);
	private manager = inject(MitpManagerService);
	private router = inject(Router);
	private jwtHelper = inject(JwtHelperService);
	private protocolService = inject(ProtocolService);
	private userService = inject(UserService);

	authStatus = new BehaviorSubject(this.isAuth());

	private magicLinkCheckFired = false;

	isAuth(): boolean {
		return sessionStorage.getItem("access_token") != null;
	}

	private setAuthSessionStorage(token: string): void {
		const helper = new JwtHelperService();
		const decodedToken = helper.decodeToken(token);
		sessionStorage.setItem("access_token", token);
		sessionStorage.setItem("email", decodedToken["username"]);
		sessionStorage.setItem("id", decodedToken["userId"]);
		sessionStorage.setItem("firstname", decodedToken["firstname"]);
		sessionStorage.setItem("lastname", decodedToken["lastname"]);
		sessionStorage.setItem("institute", decodedToken["institute"]);
		sessionStorage.setItem("institute_label", decodedToken["institute_label"]);
		sessionStorage.setItem("quality_circuit", decodedToken["quality_circuit"]);
		sessionStorage.setItem("departments", JSON.stringify(decodedToken["departments"]));
		sessionStorage.setItem("roles", JSON.stringify(decodedToken["roles"]));
		Sentry.setUser({ id: decodedToken["userId"], email: decodedToken["username"] });
		this.authStatus.next(true);
		this.protocolService.getProtocols().subscribe();
		this.protocolService.getMyProtocols().subscribe();
		this.playSignInSound();
	}

	private playSignInSound(): void {
		if (typeof Audio === "undefined") {
			return;
		}

		const audioUrl =
			typeof document === "undefined"
				? "assets/soft-startup-sound-269291.mp3"
				: new URL("assets/soft-startup-sound-269291.mp3", document.baseURI).toString();
		const audio = new Audio(audioUrl);
		audio.preload = "auto";
		void audio.play().catch(() => undefined);
	}

	login(email: string, password: string): Observable<void> {
		return this.http.post<IToken>(this.mitp.Login(), { username: email.trim(), password: password.trim() }).pipe(
			tap(({ token }) => this.setAuthSessionStorage(token)),
			map(() => void 0)
		);
	}

	getPasskeyLoginOptions(username: string): Observable<PasskeyLoginOptionsResponse> {
		return this.http.post<PasskeyLoginOptionsResponse>(this.mitp.Login() + "/passkeys/options", {
			username: username.trim(),
		});
	}

	loginWithPasskey(payload: PasskeyLoginPayload): Observable<PasskeyLoginResponse> {
		return this.http.post<PasskeyLoginResponse>(this.mitp.Login() + "/passkeys", payload).pipe(
			tap((response) => {
				if (response.status === "ok" && response.token) {
					this.setAuthSessionStorage(response.token);
				}
			})
		);
	}

	/**
	 * Asks the backend to email a login link. The API answers 202 whether or not the address
	 * belongs to an account, so callers must never derive account existence from the response.
	 */
	requestMagicLink(email: string): Observable<HttpResponse<null>> {
		return this.http.post<null>(
			this.mitp.Login() + "/magic-link",
			{ email: email.trim() },
			{ observe: "response" }
		);
	}

	/**
	 * Exchanges the credentials of an emailed magic link for a session, at most once per page
	 * load. Returns null when a check was already fired: links are single-use, so a second
	 * attempt (mail scanner, prefetch, component re-init) would only burn a valid link.
	 */
	checkMagicLinkOnce(credentials: MagicLinkCredentials): Observable<void> | null {
		if (this.magicLinkCheckFired) {
			return null;
		}
		this.magicLinkCheckFired = true;

		// Built by concatenation on purpose: HttpParams would re-encode the values and break
		// the signature check. HttpClient leaves a url untouched when no `params` are given.
		const url =
			`${this.mitp.Login()}/magic-link/check` +
			`?user=${credentials.user}&expires=${credentials.expires}&hash=${credentials.hash}`;

		return this.http.post<IToken>(url, null).pipe(
			tap(({ token }) => this.setAuthSessionStorage(token)),
			map(() => void 0)
		);
	}

	loginByToken(token: string) {
		this.setAuthSessionStorage(token);
		return of(true);
	}

	private dialog = inject(MatDialog);

	logout(returnUrl?: string) {
		this.dialog.closeAll();
		sessionStorage.clear();
		this.userService.currentUser.set(null);
		this.protocolService.invalidateProtocolsCache();
		this.protocolService.invalidateMyProtocolsCache();
		Sentry.setUser(null);
		this.authStatus.next(false);
		if (returnUrl && !returnUrl.includes("/auth")) {
			this.router.navigate(["auth"], { queryParams: { returnUrl } });
		} else {
			this.router.navigate(["auth"]);
		}
	}

	logoutIfSessionExpired(): boolean {
		if (sessionStorage.getItem("access_token") == null) {
			return false;
		}
		if (this.jwtHelper.isTokenExpired()) {
			this.logout(this.router.url);
			return true;
		}
		return false;
	}

	switch(user: User) {
		this.adminService
			.switchAccount(user.id)
			.toPromise()
			.then((response) => {
				const url = `${window.location.origin}/auth?token=${response["token"]}`;
				const nWindow = window.open(url, "_blank");
				nWindow.sessionStorage.clear();
				if (!nWindow || nWindow.closed || typeof nWindow.closed == "undefined") {
					this.manager.openInfoDialog("Veuillez désactiver le blocage du popup.");
				}
			});
	}

	isSecureLogin(): boolean {
		const token = this.jwtHelper.decodeToken();
		return "secure" in token && token["secure"] == true;
	}

	authenticatePSC() {
		return this.http.get(this.mitp.ProSanteConnect() + "/authentication");
	}

	authorizePSC(
		code: string,
		state: string,
		session_state: string,
		session: string,
		nonce: string
	): Observable<IToken> {
		return this.http.get<IToken>(this.mitp.ProSanteConnect() + "/authorization", {
			params: {
				code: code,
				state: state,
				session_state: session_state,
				session: session,
				nonce: nonce,
			},
		});
	}
}
